import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ocenaRyzyka as api } from '@/api/endpoints';
import type { RiskMeta } from '@/types';
import { Icon } from '@/components/Icon';

/**
 * Przewodnik po module oceny ryzyka: jak przejść opracowanie od paczki wodociągu do podpisanego
 * dokumentu i paczki zwrotnej. Świadomie NIE jest to statyczny tekst — parametry metodyczno-prawne
 * (terminy ustawowe, pasma ryzyka, skala, rewalidacja, lista obserwacyjna) czytamy z tego samego
 * /meta, z którego korzysta reszta modułu. Inaczej przewodnik zacząłby kłamać w dniu, w którym
 * administrator zmieni konfigurację.
 */
export function OcenaRyzykaPrzewodnik() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<RiskMeta | null>(null);
  useEffect(() => { api.meta().then(setMeta).catch(() => {}); }, []);
  const m = meta?.methodology;

  return (
    <div className="max-w-[900px]">
      <button onClick={() => navigate('/ocena-ryzyka')} className="btn btn-ghost btn-sm mb-3 text-ink-500">
        <Icon name="arrow" className="w-3.5 h-3.5 rotate-180" /> Oceny ryzyka
      </button>

      <header className="mb-5">
        <h1>Jak wykonać ocenę ryzyka</h1>
        <div className="text-ink-400 text-[13px] mt-1">
          Przewodnik po module — od materiałów od wodociągu do podpisanego dokumentu i paczki zwrotnej.
          Ustawa z 13.03.2026 (Dz.U. 2026 poz. 605), art. 4e–4i.
        </div>
      </header>

      {/* ── 0. Zanim zaczniesz ─────────────────────────────────────────────── */}
      <Sekcja tytul="Zanim zaczniesz" ikona="folder">
        <p>
          Ocena opiera się na dokumentacji, którą dostarcza dostawca wody. Zbierz ją <b>przed</b> otwarciem
          zlecenia — system czyta z niej dane, więc im więcej materiału na wejściu, tym mniej pisania ręcznie.
        </p>
        <Lista items={[
          'Dokumentacja hydrogeologiczna ujęcia i pozwolenie wodnoprawne.',
          'Wyniki badań jakości wody surowej (im dłuższy szereg, tym lepiej uzasadnisz ocenę).',
          'Schemat technologiczny: ujęcie → uzdatnianie → sieć; materiały i wiek sieci przy warstwie 4g.',
          'Informacje o strefie ochronnej: akt ustanowienia albo jego brak (to też jest ustalenie).',
          'Historia incydentów jakościowych i awarii.',
        ]} />
        <Wskazowka>
          Jeśli wodociąg pracuje w systemie HydroNexis (CSEU), poproś o <b>paczkę danych wejściowych</b> (ZIP).
          W oknie „Nowa ocena” wgrywasz ją zamiast wypełniania formularza — z manifestu powstaje warstwa,
          dostawca, studnie, zagrożenia wskazane przez wodociąg i komplet załączników.
        </Wskazowka>
      </Sekcja>

      {/* ── 1. Wybór warstwy ───────────────────────────────────────────────── */}
      <Sekcja tytul="Krok 1 · Wybierz właściwą warstwę" ikona="layout">
        <p>
          Ustawa rozdziela trzy niezależne oceny. To nie są etapy jednego opracowania — każda ma własny
          zakres, własny termin i własny dokument.
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          {(meta?.layers ?? []).map(l => (
            <div key={l.code} className="rounded border border-ink-100 p-2.5">
              <div className="font-semibold text-[13px] text-ink-900">{l.name}</div>
              <div className="text-[11px] text-ink-400 mt-0.5">{l.legalBasis}</div>
              <div className="text-[11.5px] text-ink-600 mt-1.5">{OPIS_WARSTWY[l.code] ?? ''}</div>
              <div className="text-[11px] text-ink-500 mt-1.5">
                Termin ustawowy: <span className="font-mono">{terminWarstwy(m, l.code) ?? '—'}</span>
              </div>
            </div>
          ))}
        </div>
        <Wskazowka>
          Formularz danych wejściowych pokazuje <b>tylko pola tej warstwy</b>. Jeśli widzisz pytania
          o instalację wewnętrzną w ocenie obszaru zasilania — masz wybraną złą warstwę.
        </Wskazowka>
      </Sekcja>

      {/* ── 2. Dokumenty źródłowe ──────────────────────────────────────────── */}
      <Sekcja tytul="Krok 2 · Wgraj dokumenty źródłowe" ikona="upload">
        <p>
          Pierwsza zakładka karty oceny. Przeciągnij pliki albo cały folder — system wyciąga tekst (OCR),
          indeksuje go dla AI i rozpoznaje typy dokumentów, żeby odhaczyć checklistę wymaganych materiałów.
        </p>
        <Lista items={[
          'Dokumenty zostają w archiwum — po latach wiadomo, z czego wynikła ocena.',
          'Checklista „Wymagane dokumenty” pokazuje, czego brakuje wg podstawy prawnej.',
          'To z tych plików czyta AI przy uzupełnianiu danych i pisaniu sekcji dokumentu.',
        ]} />
      </Sekcja>

      {/* ── 3. Dane wejściowe ──────────────────────────────────────────────── */}
      <Sekcja tytul="Krok 3 · Uzupełnij dane wejściowe" ikona="edit">
        <p>
          Masz dwie drogi do tych samych pól i możesz je mieszać: wpisać ręcznie albo kliknąć
          <b> „Odczytaj dane”</b> przy dokumencie źródłowym. Odczyt uzupełnia <b>wyłącznie puste pola</b> —
          nigdy nie nadpisze tego, co wpisał ekspert.
        </p>
        <Lista items={[
          'Pola oznaczone gwiazdką są krytyczne — bez nich nie ruszy generowanie oceny.',
          'Dane podstawowe zbierają identyfikację ujęcia i dostawcy: JCWPd, typ ujęcia, liczba studni, wydajność, pobór z pozwolenia.',
          'Studnie mają własne współrzędne — to one wyznaczają obszar zasilania, nie punkt technologiczny.',
          'Sekcja „Dodatkowe” to komentarz eksperta; pliki wgrywaj w dokumentach źródłowych, nie tutaj.',
        ]} />
        <Wskazowka ostrzezenie>
          Odczyt AI to <b>propozycja</b>, nie prawda objawiona. Zweryfikuj każdą wartość, zanim ruszysz dalej —
          to Ty podpisujesz dokument.
        </Wskazowka>
      </Sekcja>

      {/* ── 4. Przegląd i generowanie ──────────────────────────────────────── */}
      <Sekcja tytul="Krok 4 · Uruchom analizę i sprawdź, co system ustalił" ikona="sparkles">
        <p>
          Zakładka „Przegląd” prowadzi proces pięciu kroków: obszar zasilania → inwentarz zagrożeń →
          ocena ryzyka → środki kontroli → program badań. Każdy krok pokazuje, <b>co pobrał automatycznie</b>
          {' '}i z jakiego źródła — to jest panel proweniencji, nie czarna skrzynka.
        </p>
        <Lista items={[
          'Obszar zasilania: liczony bufor Sichardta albo wgrany/narysowany realny obszar spływu (OSW). Realny zawsze wygrywa z liczonym.',
          'Presje w zlewni pobierane są z warstw państwowych i zamieniane na propozycje zagrożeń wraz z odległością od ujęcia.',
          'Mapa poglądowa pokazuje strefę, studnie, presje i otwory PSH z CBDG — warstwa mówi wprost, gdy trzeba przybliżyć lub gdy usługa nie odpowiada.',
          'Luki do uzupełnienia to lista rzeczy, których system nie mógł ustalić — rozstrzyga je człowiek.',
        ]} />
      </Sekcja>

      {/* ── 5. Zagrożenia i ryzyko ─────────────────────────────────────────── */}
      <Sekcja tytul="Krok 5 · Zweryfikuj zagrożenia i oceń ryzyko" ikona="shield">
        <p>
          Tu jest sedno opracowania i tu ekspert pracuje najdłużej. Zagrożenia przychodzą z biblioteki
          wzorców, z warstw presji i od AI — wszystkie jako <b>propozycje do akceptacji</b>.
        </p>
        <Lista items={[
          'Opisuj zagrożenie sekwencją „X zdarza się z powodu Y” (metodyka WHO) — z takiego opisu wynika, przeciwko czemu dobierasz środki.',
          'Ryzyko początkowe (P×D) to stan bez środków; rezydualne — po ich wdrożeniu.',
          'Przy każdym zagrożeniu wpisz „Uzasadnienie oceny”: dlaczego takie P i D. Bez tego przegląd oceny po latach widzi same liczby.',
          'Środki kontroli edytujesz w miejscu; odpowiedzialnym może być dostawca, organ, obaj albo inny podmiot (wtedy podaj jego nazwę).',
          'Zagrożenie nietrafione oznacz jako odrzucone — nie kasuj go, ślad zostaje w ocenie.',
        ]} />
        {m && (
          <div className="text-[11.5px] text-ink-600 rounded bg-ink-50 border border-ink-100 p-2.5">
            Skala w tej instalacji: <b>{m.severityScale === 'KWADRATOWA' ? 'kwadratowa (P×D²)' : 'liniowa (P×D)'}</b>,
            {' '}P i D w zakresie 1–{m.matrixScaleMax}.
            {' '}Pasma wyniku: niskie ≤ {m.matrixLowMax}, średnie ≤ {m.matrixMediumMax}, wysokie ≤ {m.matrixHighMax},
            {' '}powyżej — krytyczne.
          </div>
        )}
        <Wskazowka>
          Lista nie przeskakuje w trakcie edycji, nawet gdy zmieniasz P i D. Kolejność wg ryzyka ustawisz
          przyciskiem „Sortuj wg ryzyka”, kiedy skończysz.
        </Wskazowka>
      </Sekcja>

      {/* ── 6. Monitoring ──────────────────────────────────────────────────── */}
      <Sekcja tytul="Krok 6 · Program badań i monitoring" ikona="calendar">
        <p>
          Program powstaje z zagrożeń: reguły dobierają parametry i częstotliwość, a przy wysokim ryzyku
          rezydualnym częstotliwość rośnie. Kolumna „Dlaczego ten parametr” pokazuje uzasadnienie doboru.
        </p>
        <Lista items={[
          'Monitoring operacyjny dzieli się na rodzaje: badania laboratoryjne, inspekcje terenowe, monitoring ciągły, kontrola techniczna, inne — pola dopasowują się do rodzaju.',
          'Każda pozycja powinna wskazywać środek kontroli, którego skuteczność potwierdza. Brak wskazania jest oznaczony na żółto.',
          'Monitoring zgodności to minimum ustawowe — zostaje w ocenie niezależnie od wykrytych zagrożeń.',
        ]} />
        {m && (
          <div className="text-[11.5px] text-ink-600">
            Lista obserwacyjna UE w tej instalacji: <b>{m.observationList}</b>. Katalog norm: {m.normsCatalog}.
          </div>
        )}
      </Sekcja>

      {/* ── 7. Dokument, podpis, przekazanie ───────────────────────────────── */}
      <Sekcja tytul="Krok 7 · Dokument, recenzja, podpis" ikona="file-text">
        <p>
          Sekcje narracyjne generuje AI, ugruntowana faktami z oceny — każde zdanie ma pokrycie w danych,
          a nie w wyobraźni modelu. Sekcję możesz zaakceptować albo poprawić ręcznie.
        </p>
        <Lista items={[
          'Przekazanie do recenzji zmienia status i powiadamia recenzenta — przypisz go wcześniej w karcie „Zespół”.',
          'Podpis tworzy finalny PDF; od tego momentu ocena jest zamknięta do edycji.',
          `Rewalidacja: kolejny przegląd oceny po ${m?.revalidationYears ?? 6} latach (art. 4e ust. 2).`,
        ]} />
      </Sekcja>

      {/* ── 8. Paczka do ckes ──────────────────────────────────────────────── */}
      <Sekcja tytul="Krok 8 · Oddaj wynik wodociągowi" ikona="download">
        <p>
          Po podpisie w „Przeglądzie” pojawia się <b>„Paczka do ckes”</b> — ZIP z manifestem i podpisanym
          dokumentem. Wodociąg wgrywa go u siebie w HydroNexis („Ocena ryzyka → Importuj wynik”) i dostaje
          gotową książkę eksploatacji: harmonogram monitoringu, rejestr działań i alerty terminowe.
        </p>
        <Wskazowka>
          Import po tamtej stronie przyjmuje wynik <b>raz na cykl</b>. Zanim wyślesz paczkę, upewnij się,
          że dokument jest ostateczny — kolejna wersja wymaga u klienta nowej oceny.
        </Wskazowka>
      </Sekcja>

      {/* ── Najczęstsze błędy ──────────────────────────────────────────────── */}
      <Sekcja tytul="Najczęstsze błędy" ikona="bell">
        <div className="grid gap-2">
          {BLEDY.map(b => (
            <div key={b.tytul} className="rounded border border-ink-100 p-2.5">
              <div className="font-semibold text-[12.5px] text-ink-900">{b.tytul}</div>
              <div className="text-[12px] text-ink-600 mt-0.5">{b.opis}</div>
            </div>
          ))}
        </div>
      </Sekcja>

      <div className="card mb-6"><div className="card-body flex items-center gap-3 flex-wrap">
        <span className="text-[13px] text-ink-600">Gotowy, żeby zacząć?</span>
        <Link to="/ocena-ryzyka" className="btn btn-primary btn-sm">
          <Icon name="arrow" className="w-3.5 h-3.5" /> Przejdź do ocen ryzyka
        </Link>
      </div></div>
    </div>
  );
}

// ==================== elementy pomocnicze ====================

function Sekcja({ tytul, ikona, children }: { tytul: string; ikona: string; children: React.ReactNode }) {
  return (
    <div className="card mb-4">
      <div className="card-head flex items-center gap-2">
        <Icon name={ikona} className="w-4 h-4 text-water-600 shrink-0" />
        <span className="card-title">{tytul}</span>
      </div>
      <div className="card-body grid gap-2.5 text-[13px] text-ink-700 leading-relaxed">{children}</div>
    </div>
  );
}

function Lista({ items }: { items: string[] }) {
  return (
    <ul className="grid gap-1.5">
      {items.map(t => (
        <li key={t} className="flex gap-2">
          <span className="text-water-500 shrink-0 mt-[3px]">•</span>
          <span className="text-[12.5px] text-ink-600">{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Wskazowka({ children, ostrzezenie = false }: { children: React.ReactNode; ostrzezenie?: boolean }) {
  return (
    <div className={'rounded border-l-4 p-2.5 text-[12px] '
      + (ostrzezenie ? 'border-l-amber-400 bg-amber-50/60 text-ink-700' : 'border-l-water-400 bg-water-50/50 text-ink-700')}>
      {children}
    </div>
  );
}

const OPIS_WARSTWY: Record<string, string> = {
  UJECIE: 'Zlewnia, z której woda dopływa do studni: presje w terenie, jakość wody surowej, strefa ochronna.',
  SYSTEM: 'Droga wody od ujęcia do punktu zgodności: uzdatnianie, zbiorniki, sieć, liczba zaopatrywanej ludności.',
  INSTALACJA: 'Instalacja w budynku: priorytetowo Legionella (c.w.u.) i ołów (materiały przyłączy).',
};

function terminWarstwy(m: RiskMeta['methodology'] | undefined, kod: string): string | null {
  if (!m) return null;
  if (kod === 'UJECIE') return m.deadlineCatchment;
  if (kod === 'SYSTEM') return m.deadlineSystem;
  if (kod === 'INSTALACJA') return m.deadlineInstallation;
  return null;
}

const BLEDY: { tytul: string; opis: string }[] = [
  {
    tytul: 'Generowanie oceny przed wgraniem dokumentów',
    opis: 'Analiza pracuje wtedy na pustym materiale i produkuje ogólniki. Przegląd mówi wprost, że czeka na dane wejściowe — najpierw dokumenty, potem generowanie.',
  },
  {
    tytul: 'Akceptacja propozycji AI bez weryfikacji',
    opis: 'Zagrożenia, wartości P i D oraz uzasadnienia są propozycjami. Odpowiada za nie osoba podpisująca ocenę, nie system.',
  },
  {
    tytul: 'Liczony okrąg zamiast realnego obszaru spływu',
    opis: 'Bufor Sichardta to przybliżenie na start. Jeśli w dokumentacji jest wyznaczony obszar spływu (OSW), wgraj go — zmienia listę presji, a więc i całą ocenę.',
  },
  {
    tytul: 'Zagrożenie bez środka kontroli i bez uzasadnienia',
    opis: 'Taki wiersz nie mówi ani co robimy z ryzykiem, ani skąd wzięła się jego wartość. To pierwsze, o co zapyta recenzent i organ.',
  },
  {
    tytul: 'Mylenie warstw 4f i 4g',
    opis: 'Liczba zaopatrywanej ludności czy materiały sieci opisują system zaopatrzenia (4g), nie obszar zasilania (4f). Dla 4f liczy się zlewnia i jakość wody surowej.',
  },
];
